import { CSSObject } from '@emotion/react';
import * as React from 'react';
import { Size } from '../Button/Button';
import { Aria, LoadingButton } from '../index';

type IconOnlyButtonProps = {
  /** The text that will be read via assistive technologies. */
  aria: string;

  /** The `svg` to display for the icon only button. */
  children: React.ReactNode;

  /** A icon only button doesn't support loading. */
  isLoading?: undefined;

  /** The aesthetic to apply to the button. */
  variant: 'fab' | 'icon-only';
};

type IconWithTextButtonProps = {
  /** ARIA content isn't needed with icon text buttons. */
  aria?: undefined;

  /** The `svg` and text to display for the icon text button. */
  children: React.ReactNode;

  /** Displays a spinner instead of the normal content. */
  isLoading: Parameters<typeof LoadingButton>[0]['isLoading'];

  /** The aesthetic to apply to the button. */
  variant: 'secondary' | 'tertiary';
};

type IconButtonProps = (IconOnlyButtonProps | IconWithTextButtonProps) & {
  /** CSS class names. */
  className?: string;

  /** Disables the button's interactivity. */
  disabled?: Parameters<typeof LoadingButton>[0]['disabled'];

  /** A unique identifier for the button. */
  id?: string;

  /** The callback to invoke when the button is clicked. */
  onClick?: Parameters<typeof LoadingButton>[0]['onClick'];

  /** The desired height and width of the icon text button. */
  size: Size;

  /** The default behavior of the button. */
  type?: Parameters<typeof LoadingButton>[0]['type'];

  /** An optional, custom icon size */
  iconSize?: number;
};

const btnSizeToIconSizeMap: Record<Size, string> = {
  large: '24px',
  small: '16px',
};

const iconOnlyButtonVariantStyles: Record<
  IconOnlyButtonProps['variant'],
  CSSObject
> = {
  'icon-only': {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: '#4B585D',
    cursor: 'pointer',
    lineHeight: 0,
    padding: 0,
    '&:disabled': {
      color: 'rgba(75, 88, 93, 0.5)',
      cursor: 'default',
    },
    '&:hover:not(:disabled)': {
      backgroundColor: 'rgba(215, 218, 223, 1)',
    },
    '&:active:not(:disabled)': {
      backgroundColor: 'rgba(215, 218, 223, 0.7)',
      color: 'rgba(75, 88, 93, 0.7)',
    },
  },
  fab: {
    backgroundColor: '#fff',
    border: 'none',
    borderRadius: '3px',
    boxShadow: '0 2px 16px 0 rgba(0, 116, 204, 0.08)',
    color: '#4B585D',
    cursor: 'pointer',
    lineHeight: 0,
    padding: '10px',
    '&:disabled': {
      color: 'rgba(75, 88, 93, 0.5)',
      cursor: 'default',
    },
    '&:hover:not(:disabled)': {
      backgroundColor: '#DCDFE3',
    },
    '&:active:not(:disabled)': {
      backgroundColor: '#DCDFE3',
      color: 'rgba(75, 88, 93, 0.75)',
    },
  },
};

const IconButton: React.FC<IconButtonProps> = ({
  aria,
  children,
  className,
  disabled,
  id,
  isLoading,
  onClick,
  size,
  type,
  variant,
  iconSize,
}) => {
  if (variant === 'fab' || variant === 'icon-only') {
    const sizeInPixels = iconSize
      ? `${iconSize}px`
      : btnSizeToIconSizeMap[size];

    return (
      <button
        className={className}
        css={iconOnlyButtonVariantStyles[variant]}
        disabled={disabled}
        id={id}
        onClick={onClick}
        type={type}
      >
        <Aria>{aria}</Aria>
        {React.cloneElement(React.Children.only(children) as any, {
          height: sizeInPixels,
          width: sizeInPixels,
        })}
      </button>
    );
  }

  const isIconAndText = React.Children.count(children) === 2;

  if (!isIconAndText) {
    return null;
  }

  return (
    <LoadingButton
      className={className}
      disabled={disabled}
      id={id}
      isLoading={isLoading === undefined ? false : isLoading}
      onClick={onClick}
      size={size}
      type={type}
      variant={variant}
    >
      {React.Children.map(children, (child, index) => {
        if (index !== 0 || !React.isValidElement(child)) {
          return child;
        }

        return React.cloneElement(child, {
          height: '24px',
          style: { marginRight: '4px' },
          width: '24px',
        });
      })}
    </LoadingButton>
  );
};

export default IconButton;
